import { axiosHub } from '@/services/config/axiosHub.config'
import {
  NotificationSetupTemplate,
  NotificationMessageTemplate,
  OrganizationNotificationTrigger,
  NotificationDispatchSetup,
  NotificationEngineSourceDTO,
  PaginatedNotificationTriggerDTO,
  PaginatedNotificationSetupTemplateDTO
} from './types'

// Notification Setup Templates
const fetchSetupTemplates = async (params: {
  name?: string
  source?: string
  page?: number
  itemsPerPage?: number
  includeInactive?: boolean
}) => {
  const response = await axiosHub.get<PaginatedNotificationSetupTemplateDTO>(
    '/api/v1/notifications/setup-templates',
    { params }
  )
  return response.data
}

const fetchSetupTemplateDetails = async (id: string) => {
  const response = await axiosHub.get<NotificationSetupTemplate>(
    `/api/v1/notifications/setup-templates/${id}`
  )
  return response.data
}

const createSetupTemplate = async (data: Partial<NotificationSetupTemplate>) => {
  const response = await axiosHub.post<NotificationSetupTemplate>(
    '/api/v1/notifications/setup-templates',
    data
  )
  return response.data
}

const updateSetupTemplate = async (id: string, data: Partial<NotificationSetupTemplate>) => {
  const response = await axiosHub.put<NotificationSetupTemplate>(
    `/api/v1/notifications/setup-templates/${id}`,
    data
  )
  return response.data
}

const deleteSetupTemplate = async (id: string) => {
  await axiosHub.delete(`/api/v1/notifications/setup-templates/${id}`)
}

// Notification Message Templates
const fetchMessageTemplates = async (params: {
  name?: string
  organizationId?: string
  includeGlobalTemplates?: boolean
  searchChannels?: string
}) => {
  const response = await axiosHub.get<{ items: NotificationMessageTemplate[]; total: number }>(
    '/api/v1/notifications/templates',
    { params }
  )
  return response.data
}

const createMessageTemplate = async (data: Partial<NotificationMessageTemplate>) => {
  const response = await axiosHub.post<NotificationMessageTemplate>(
    '/api/v1/notifications/templates',
    data
  )
  return response.data
}

const updateMessageTemplate = async (id: string, data: Partial<NotificationMessageTemplate>) => {
  const response = await axiosHub.put<NotificationMessageTemplate>(
    `/api/v1/notifications/templates/${id}`,
    data
  )
  return response.data
}

const deleteMessageTemplate = async (id: string) => {
  await axiosHub.delete(`/api/v1/notifications/templates/${id}`)
}

// Organization Notification Triggers
const fetchTriggers = async (params: {
  organizationId: string
  name?: string
  source?: string
  page?: number
  itemsPerPage?: number
}) => {
  const response = await axiosHub.get<PaginatedNotificationTriggerDTO>(
    '/api/v1/notifications/triggers',
    { params }
  )
  return response.data
}

const fetchTriggerDetails = async (id: string) => {
  const response = await axiosHub.get<OrganizationNotificationTrigger>(
    `/api/v1/notifications/triggers/${id}`
  )
  return response.data
}

const createTrigger = async (data: {
  // Atributos principais para esta feature
  organizationId: string
  notificationSetupTemplateId?: string
  recipientUserIds?: string[]
  
  // Todos os outros atributos do endpoint
  name?: string
  description?: string
  source?: string
  conditions?: any
  mongoQuery?: Record<string, any>
  isActive?: boolean
  notificationMessageTemplateId?: string
  additionalPayloadDefinitionConfig?: {
    sendingBehavior: string
  }
}) => {
  const response = await axiosHub.post<OrganizationNotificationTrigger>(
    '/api/v1/notifications/triggers',
    data
  )
  return response.data
}

const updateTrigger = async (id: string, data: Partial<OrganizationNotificationTrigger>) => {
  const response = await axiosHub.put<OrganizationNotificationTrigger>(
    `/api/v1/notifications/triggers/${id}`,
    data
  )
  return response.data
}

const deleteTrigger = async (id: string) => {
  await axiosHub.delete(`/api/v1/notifications/triggers/${id}`)
}

const updateDispatchSetup = async (id: string, data: Pick<NotificationDispatchSetup, 'recipientUserIds' | 'additionalPayloadDefinitionConfig'>) => {
  const response = await axiosHub.put<NotificationDispatchSetup>(
    `/api/v1/notifications/dispatch-setups/${id}`,
    data
  )
  return response.data
}

const deleteDispatchSetup = async (id: string) => {
  await axiosHub.delete(`/api/v1/notifications/dispatch-setups/${id}`)
}

const fetchEngineSourceFields = async (triggerSource: string, organizationId?: string) => {
  const response = await axiosHub.get<NotificationEngineSourceDTO[]>(
    '/api/v1/notifications/engine-sources/fields',
    {
      params: { triggerSource, organizationId }
    }
  )
  return response.data
}

const fetchTriggerSources = async () => {
  const response = await axiosHub.get<{
    eventsByTriggerSources: any[]
    triggerSourcesByEvents: any[]
  }>('/api/v1/notifications/triggers/sources')
  return response.data
}

const triggers = {
  fetch: fetchTriggers,
  fetchDetails: fetchTriggerDetails,
  create: createTrigger,
  update: updateTrigger,
  delete: deleteTrigger,
  sources: fetchTriggerSources
}

const setupTemplates = {
  fetch: fetchSetupTemplates,
  fetchDetails: fetchSetupTemplateDetails,
  create: createSetupTemplate,
  update: updateSetupTemplate,
  delete: deleteSetupTemplate
}

const templates = {
  fetch: fetchMessageTemplates,
  create: createMessageTemplate,
  update: updateMessageTemplate,
  delete: deleteMessageTemplate
}

const dispatchSetups = {
  update: updateDispatchSetup,
  delete: deleteDispatchSetup
}

const engineSources = {
  fetchFields: fetchEngineSourceFields
}

export const Notifications = {
  triggers,
  setupTemplates,
  templates,
  dispatchSetups,
  engineSources
}
